import React, { useMemo, useState } from 'react';
import { X, Download, Trash, Search } from 'lucide-react';
import ConfirmPopup from './ConfirmPopup';

export interface DataRow {
  id: string;
  email: string;
  name: string;
  segment: string;
  ltv: string;
  updated_at: string;
}

interface CapturedDataModalProps {
  open: boolean;
  onClose: () => void;
  scheduleName: string;
  rows: DataRow[];
  onEraseAll: () => void;
  onExportCsv: () => void;
  onDeleteRow: (id: string) => void;
}

const SEGMENTS = ['All', 'enterprise', 'smb', 'mid-market', 'trial'];

const CapturedDataModal: React.FC<CapturedDataModalProps> = ({
  open,
  onClose,
  scheduleName,
  rows,
  onEraseAll,
  onExportCsv,
  onDeleteRow,
}) => {
  const [search, setSearch] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [confirmErase, setConfirmErase] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const filteredRows = useMemo(() => {
    let list = rows;
    const q = search.toLowerCase().trim();
    if (q) {
      list = list.filter(
        (r) =>
          r.id.toLowerCase().includes(q) ||
          r.email.toLowerCase().includes(q) ||
          r.name.toLowerCase().includes(q) ||
          r.segment.toLowerCase().includes(q)
      );
    }
    if (segmentFilter !== 'All') {
      list = list.filter((r) => r.segment === segmentFilter);
    }
    if (dateFrom) {
      list = list.filter((r) => r.updated_at >= dateFrom);
    }
    if (dateTo) {
      list = list.filter((r) => r.updated_at <= dateTo);
    }
    return list;
  }, [rows, search, segmentFilter, dateFrom, dateTo]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ fontFamily: 'var(--font-geist-sans, "Inter"), system-ui, sans-serif' }}
      >
        {/* Blurred backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-md transition-opacity"
          onClick={onClose}
          aria-hidden
        />
        {/* Modal panel — 80% of viewport, centered */}
        <div
          className="relative w-[80vw] h-[80vh] flex flex-col rounded-2xl border border-white/10 bg-neutral-900/95 shadow-2xl shadow-black/40 overflow-hidden backdrop-blur-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-white/5 flex-shrink-0 bg-gradient-to-b from-white/[0.03] to-transparent">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-white" style={{ fontFamily: 'var(--font-geist-sans), "Inter", sans-serif', letterSpacing: '-0.02em' }}>
                Captured Data
              </h2>
              <p className="text-sm text-zinc-500 mt-1 font-medium tracking-wide" style={{ letterSpacing: '0.02em' }}>
                {scheduleName}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-white rounded-xl hover:bg-white/10 transition-all duration-200"
              aria-label="Close"
            >
              <X className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 px-6 py-4 border-b border-white/5 flex-shrink-0 bg-white/[0.02]">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" strokeWidth={2} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by ID, email, name..."
                className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-zinc-500 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-all font-medium"
                style={{ letterSpacing: '0.01em' }}
              />
            </div>
            <select
              value={segmentFilter}
              onChange={(e) => setSegmentFilter(e.target.value)}
              className="px-4 py-2.5 text-sm rounded-xl bg-white/5 border border-white/10 text-white outline-none focus:border-indigo-500/60 font-medium"
              style={{ letterSpacing: '0.02em' }}
            >
              {SEGMENTS.map((s) => (
                <option key={s} value={s}>
                  Segment: {s}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2.5 text-sm rounded-xl bg-white/5 border border-white/10 text-white outline-none focus:border-indigo-500/60 font-medium"
              />
              <span className="text-zinc-500 text-sm font-medium">–</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2.5 text-sm rounded-xl bg-white/5 border border-white/10 text-white outline-none focus:border-indigo-500/60 font-medium"
              />
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-zinc-500 font-medium tabular-nums" style={{ letterSpacing: '0.05em' }}>
                {filteredRows.length} of {rows.length} rows
              </span>
              <button
                type="button"
                onClick={() => setConfirmErase(true)}
                className="inline-flex items-center gap-2 py-2 px-3.5 text-xs font-semibold rounded-xl border border-white/10 text-zinc-400 hover:border-red-500/50 hover:text-red-400 hover:bg-red-500/10 transition-all"
                style={{ letterSpacing: '0.03em' }}
              >
                <Trash className="w-3.5 h-3.5" />
                Erase All
              </button>
              <button
                type="button"
                onClick={onExportCsv}
                className="inline-flex items-center gap-2 py-2 px-3.5 text-xs font-semibold rounded-xl border border-white/10 text-zinc-400 hover:border-indigo-500/50 hover:text-indigo-400 hover:bg-indigo-500/10 transition-all"
                style={{ letterSpacing: '0.03em' }}
              >
                <Download className="w-3.5 h-3.5" />
                Export CSV
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto min-h-0">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10 bg-neutral-900/98 backdrop-blur-sm border-b border-white/5">
                <tr>
                  <th className="text-left text-[11px] uppercase tracking-widest text-zinc-500 font-semibold py-3.5 px-4" style={{ letterSpacing: '0.12em' }}>
                    id
                  </th>
                  <th className="text-left text-[11px] uppercase tracking-widest text-zinc-500 font-semibold py-3.5 px-4" style={{ letterSpacing: '0.12em' }}>
                    email
                  </th>
                  <th className="text-left text-[11px] uppercase tracking-widest text-zinc-500 font-semibold py-3.5 px-4" style={{ letterSpacing: '0.12em' }}>
                    name
                  </th>
                  <th className="text-left text-[11px] uppercase tracking-widest text-zinc-500 font-semibold py-3.5 px-4" style={{ letterSpacing: '0.12em' }}>
                    segment
                  </th>
                  <th className="text-left text-[11px] uppercase tracking-widest text-zinc-500 font-semibold py-3.5 px-4" style={{ letterSpacing: '0.12em' }}>
                    ltv
                  </th>
                  <th className="text-left text-[11px] uppercase tracking-widest text-zinc-500 font-semibold py-3.5 px-4" style={{ letterSpacing: '0.12em' }}>
                    updated_at
                  </th>
                  <th className="w-12 border-b border-white/5" />
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-white/[0.04] border-b border-white/5 transition-colors duration-150"
                  >
                    <td className="py-3 px-4 text-sm text-zinc-400 font-mono tabular-nums">{row.id}</td>
                    <td className="py-3 px-4 text-sm text-zinc-400 font-mono">{row.email}</td>
                    <td className="py-3 px-4 text-sm text-zinc-100 font-medium">{row.name}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className="inline-block bg-white/10 text-zinc-300 px-2.5 py-1 rounded-lg text-xs font-semibold" style={{ letterSpacing: '0.02em' }}>
                        {row.segment}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-zinc-200 font-medium tabular-nums">{row.ltv}</td>
                    <td className="py-3 px-4 text-sm text-zinc-500 font-mono tabular-nums">{row.updated_at}</td>
                    <td className="py-3 px-4">
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(row.id)}
                        className="p-1.5 text-zinc-500 hover:text-red-400 rounded-lg hover:bg-red-500/10 transition-all"
                        title="Delete row"
                      >
                        <X className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredRows.length === 0 && (
              <div className="py-16 text-center">
                <p className="text-sm text-zinc-500 font-medium" style={{ letterSpacing: '0.02em' }}>
                  No rows match the filters.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmPopup
        open={confirmErase}
        onClose={() => setConfirmErase(false)}
        title="Erase all data?"
        message="This will remove all captured data rows for this schedule. This action cannot be undone."
        confirmLabel="Erase All"
        variant="danger"
        onConfirm={() => {
          onEraseAll();
          setConfirmErase(false);
        }}
      />
      <ConfirmPopup
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        title="Delete row?"
        message="This row will be removed from the captured data."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (confirmDeleteId) onDeleteRow(confirmDeleteId);
          setConfirmDeleteId(null);
        }}
      />
    </>
  );
};

export default CapturedDataModal;
